import { api_configuration } from "../../common";
import api_client_state = api_configuration.types.client_state.alan_api;
import api_application = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.application.alan_api;
import {
	julian_datetime_2_gregorian_datetime,
	julian_datetime_2_js_date,
	julian_date_2_gregorian_date,
	julian_date_2_js_date,
} from "../calendar/conversion";
import { Languages, translate } from "../front/translate";
import { applyToConversion } from "../widgets/number_converter";
import { numberToLocaleString } from "../utils";
import { gui_model } from "../state/ClientState";

const number_of_second_per_minute = 60;
const number_of_minutes_per_hour = 60;
const number_of_seconds_per_hour = number_of_second_per_minute * number_of_minutes_per_hour;

function pad(str: string, width: number) {
	const len = Math.max(0, width - str.length);
	let i: number;
	let padded_string = str;
	for (i = 0; i < len; i += 1) {
		padded_string = "0" + padded_string;
	}
	return padded_string;
}
function mod(x: number, n: number) {
	return x % n;
}
function nr_of_digits(value: number) {
	const value_parts = value.toString().split(".");
	let digits = 0;
	if (value_parts.length > 1) {
		digits = value_parts[1].length;
	}
	return digits;
}
export function serializeHours(hours: number, language?: Languages): string {
	let text_hours = "";
	const negative_parity = hours < 0;
	if (negative_parity) {
		text_hours += "-";
	}
	const digits = nr_of_digits(hours);
	const nr_of_hours = Number(hours.toFixed(digits));
	text_hours += nr_of_hours + translate("hour (short)", language);

	return text_hours;
}
export function serializeMinutes(minutes: number, language?: Languages): string {
	let text_minutes = "";
	const negative_parity = minutes < 0;
	if (negative_parity) {
		text_minutes += "-";
	}
	const abs_minutes = Math.abs(minutes);
	let has_previous_part = false;
	const digits = nr_of_digits(minutes);

	const nr_of_hours = Math.floor(abs_minutes / number_of_minutes_per_hour);
	const minutes_left = mod(abs_minutes, number_of_minutes_per_hour);
	const nr_of_minutes = Number(minutes_left.toFixed(digits));
	if (nr_of_hours !== 0) {
		text_minutes += nr_of_hours + translate("hour (short)", language);
		has_previous_part = true;
	}
	if (has_previous_part) {
		text_minutes += pad("" + nr_of_minutes, 2) + translate("minutes (short)", language);
	} else {
		text_minutes += nr_of_minutes + translate("minutes (short)", language);
	}

	return text_minutes;
}
export function serializeSeconds(seconds: number, language?: Languages): string {
	let text_seconds = "";
	const negative_parity = seconds < 0;
	if (negative_parity) {
		text_seconds += "-";
	}
	const digits = nr_of_digits(seconds);
	const abs_seconds = Math.abs(seconds);
	let has_previous_part = false;

	const nr_of_hours = Math.floor(abs_seconds / number_of_seconds_per_hour);
	let seconds_left = mod(abs_seconds, number_of_seconds_per_hour);
	const nr_of_minutes = Math.floor(seconds_left / number_of_second_per_minute);
	seconds_left = mod(seconds_left, number_of_second_per_minute);
	const nr_of_seconds = Number(seconds_left.toFixed(digits));
	if (nr_of_hours !== 0) {
		text_seconds += nr_of_hours + translate("hour (short)", language);
		has_previous_part = true;
	}
	if (has_previous_part) {
		text_seconds += pad("" + nr_of_minutes, 2) + translate("minutes (short)", language);
	} else {
		if (nr_of_minutes !== 0) {
			text_seconds += nr_of_minutes + translate("minutes (short)", language);
			has_previous_part = true;
		}
	}
	if (has_previous_part) {
		text_seconds += pad("" + nr_of_seconds, 2) + translate("seconds (short)", language);
	} else {
		text_seconds += "" + nr_of_seconds + translate("seconds (short)", language);
	}

	return text_seconds;
}

export function getDecimalsFromNumericalTypeProperties(
	numerical_type_properties: api_client_state.Cnumerical_type_properties
): number {
	switch (numerical_type_properties.properties.initialized.state.name) {
		case "no": {
			return 0;
		}
		case "yes": {
			const $ = numerical_type_properties.properties.initialized.state.node;
			return $.properties.decimals.value;
		}
		default:
			unexpectedState(numerical_type_properties.properties.initialized.state);
	}
}

export function getStaticDecimalsFromNumericalType(numerical_type: api_application.Cnumerical_types): number {
	switch (numerical_type.properties.ui.properties.represent_as.state.name) {
		case "custom type": {
			const custom = numerical_type.properties.ui.properties.represent_as.state.node;
			switch (custom.properties.properties.properties.conversion.state.name) {
				case "none": {
					return 0;
				}
				case "point translation": {
					const point_translation = custom.properties.properties.properties.conversion.state.node;
					switch (point_translation.properties.decimals.properties.type.state.name) {
						case "dynamic": {
							return 0;
						}
						case "number": {
							const number = point_translation.properties.decimals.properties.type.state.node;
							switch (number.properties.type.state.name) {
								case "binary expression": {
									return 0;
								}
								case "constant": {
									const $ = number.properties.type.state.node;
									return $.properties.value.properties.value.value;
								}
								case "list expression": {
									return 0;
								}
								case "now in seconds": {
									return 0;
								}
								case "unary expression": {
									return 0;
								}
								default:
									return unexpectedState(number.properties.type.state);
							}
						}
						case "state": {
							return 0;
						}
						case "sticky": {
							return 0;
						}
						case "style": {
							return 0;
						}
						case "text": {
							return 0;
						}
						default:
							return unexpectedState(point_translation.properties.decimals.properties.type.state);
					}
				}
				default:
					return unexpectedState(custom.properties.properties.properties.conversion.state);
			}
		}
		case "date": {
			return 0;
		}
		case "date and time": {
			return 0;
		}
		case "duration": {
			return 0;
		}
		case "model": {
			return 0;
		}
		default:
			return unexpectedState(numerical_type.properties.ui.properties.represent_as.state);
	}
}

export function serializeNumber(
	num: number,
	decimals: number,
	numerical_type: api_application.Cnumerical_types,
	language?: Languages,
	no_locale = false
): string {
	const gui_numerical_type = gui_model.root.properties.numerical_types.get(numerical_type.key);
	if (gui_numerical_type) {
		switch (gui_numerical_type.properties.representation_type.state.name) {
			case "model": {
				if (no_locale) {
					return applyToConversion(num, decimals).toString();
				} else {
					return numberToLocaleString(applyToConversion(num, decimals));
				}
			}
			case "date and time": {
				const max_serializable_julian_time = 8850866716800;
				const min_serializable_julian_time = -8429133283200;
				if (num <= max_serializable_julian_time && num >= min_serializable_julian_time) {
					if (no_locale) {
						return julian_datetime_2_gregorian_datetime(num).canonicalformat();
					} else {
						return julian_datetime_2_js_date(num).toLocaleString();
					}
				}
				return translate("out of range");
			}
			case "HTML date and time": {
				const max_serializable_julian_time = 8850866716800;
				const min_serializable_julian_time = -8429133283200;
				if (num <= max_serializable_julian_time && num >= min_serializable_julian_time) {
					return julian_datetime_2_gregorian_datetime(num).htmlformat();
				}
				return translate("out of range");
			}
			case "date": {
				const max_serializable_julian_date = 102440587;
				const min_serializable_julian_date = -97559413;
				if (num <= max_serializable_julian_date && num >= min_serializable_julian_date) {
					if (no_locale) {
						return julian_date_2_gregorian_date(num).canonicalformat();
					} else {
						return julian_date_2_js_date(num).toLocaleDateString();
					}
				}
				return translate("out of range");
			}
			case "duration": {
				const duration = gui_numerical_type.properties.representation_type.state.node;
				switch (duration.properties.unit.state.name) {
					case "seconds": {
						return serializeSeconds(num, language);
					}
					case "minutes": {
						return serializeMinutes(num, language);
					}
					case "hours": {
						return serializeHours(num, language);
					}
				}
			}
		}
	} else {
		if (no_locale) {
			return applyToConversion(num, decimals).toString();
		} else {
			return numberToLocaleString(applyToConversion(num, decimals));
		}
	}
}
